/**
 * Google Time Zone API Integration Utility
 * Provides timezone information based on geographic coordinates
 */

import { useState, useEffect } from 'react';

// TypeScript interfaces for backend timezone API
interface BackendTimeZoneResponse {
  timezone: string;
  timeZoneName?: string;
  dstOffset?: number;
  rawOffset?: number;
  status: 'success' | 'error';
  error?: string;
  api_status?: string;
}

interface TimeZoneError {
  message: string;
  code?: string;
  status: 'error';
}

interface TimeZoneResult {
  timezone: string;
  dstOffset: number;
  rawOffset: number;
  status: 'success' | 'error';
  error?: string;
}

// Environment variables
const TIMEZONE_API_ENDPOINT = '/api/timezone';

/**
 * Fetches timezone information for given coordinates using Google Time Zone API
 * 
 * @param latitude - Geographic latitude in decimal degrees
 * @param longitude - Geographic longitude in decimal degrees
 * @param timestamp - Unix timestamp (defaults to current time)
 * @returns Promise resolving to TimeZoneResult object
 */
export async function fetchTimezone(
  latitude: number,
  longitude: number,
  timestamp: number = Math.floor(Date.now() / 1000)
): Promise<TimeZoneResult> {
  console.group('🌍 FETCHING TIMEZONE DATA');
  console.log('📍 Input coordinates:', { latitude, longitude });
  console.log('⏰ Timestamp:', timestamp, `(${new Date(timestamp * 1000).toISOString()})`);

  // Validate coordinates
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    console.error('❌ Invalid coordinates provided');
    console.groupEnd();
    return {
      timezone: 'UTC',
      dstOffset: 0,
      rawOffset: 0,
      status: 'error',
      error: 'Invalid coordinates provided'
    };
  }

  if (latitude < -90 || latitude > 90) {
    console.error('❌ Invalid latitude:', latitude);
    console.groupEnd();
    return {
      timezone: 'UTC',
      dstOffset: 0,
      rawOffset: 0,
      status: 'error',
      error: 'Latitude must be between -90 and 90 degrees'
    };
  }

  if (longitude < -180 || longitude > 180) {
    console.error('❌ Invalid longitude:', longitude);
    console.groupEnd();
    return {
      timezone: 'UTC',
      dstOffset: 0,
      rawOffset: 0,
      status: 'error',
      error: 'Longitude must be between -180 and 180 degrees'
    };
  }

  try {
    // Construct API URL with parameters
    const baseUrl = window.location.origin;
    const url = new URL(TIMEZONE_API_ENDPOINT, baseUrl);
    url.searchParams.append('lat', latitude.toString());
    url.searchParams.append('lng', longitude.toString());
    url.searchParams.append('timestamp', timestamp.toString());

    console.log('🌐 Making API request to:', url.toString());
    console.log('📤 Request parameters:', {
      lat: latitude,
      lng: longitude,
      timestamp: timestamp
    });

    // Make the API request
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('📥 Response status:', response.status);
    console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: BackendTimeZoneResponse = await response.json();
    
    console.log('📊 Raw API Response:', data);
    console.log('📊 Response status:', data.status);
    console.log('📊 Timezone ID:', data.timezone);
    console.log('📊 Timezone Name:', data.timeZoneName);
    console.log('📊 DST Offset:', data.dstOffset, 'seconds');
    console.log('📊 Raw Offset:', data.rawOffset, 'seconds');

    // Check for backend API errors
    if (data.status === 'error') {
      const errorMessage = data.error || 'Unknown API error';
      console.error('❌ Backend Timezone API Error:', errorMessage);
      console.groupEnd();
      
      return {
        timezone: 'UTC',
        dstOffset: 0,
        rawOffset: 0,
        status: 'error',
        error: errorMessage
      };
    }

    // Validate response data
    if (!data.timezone) {
      console.error('❌ No timezone ID in response');
      console.groupEnd();
      return {
        timezone: 'UTC',
        dstOffset: 0,
        rawOffset: 0,
        status: 'error',
        error: 'No timezone information received from API'
      };
    }

    const result: TimeZoneResult = {
      timezone: data.timezone,
      dstOffset: data.dstOffset || 0,
      rawOffset: data.rawOffset || 0,
      status: 'success'
    };

    console.log('✅ Timezone data processed successfully');
    console.log('📋 Final result:', result);
    console.groupEnd();

    return result;

  } catch (error) {
    console.group('❌ TIMEZONE FETCH ERROR');
    console.error('❌ Error details:', error);
    console.error('❌ Error message:', error instanceof Error ? error.message : 'Unknown error');
    console.error('❌ Error stack:', error instanceof Error ? error.stack : 'No stack trace');
    console.groupEnd();

    return {
      timezone: 'UTC',
      dstOffset: 0,
      rawOffset: 0,
      status: 'error',
      error: error instanceof Error ? error.message : 'Failed to fetch timezone data'
    };
  }
}

/**
 * React hook for fetching timezone data with loading and error states
 * 
 * @param latitude - Geographic latitude
 * @param longitude - Geographic longitude
 * @param enabled - Whether to automatically fetch data (default: true)
 * @returns Object containing timezone data, loading state, and error state
 */
export function useTimezone(
  latitude: number | null,
  longitude: number | null,
  enabled: boolean = true
) {
  const [timezoneData, setTimezoneData] = useState<TimeZoneResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (lat: number, lng: number) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('🔄 useTimezone: Fetching timezone for:', { lat, lng });
      const result = await fetchTimezone(lat, lng);
      
      setTimezoneData(result);
      
      if (result.status === 'error') {
        setError(result.error || 'Failed to fetch timezone');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('❌ useTimezone: Error fetching timezone:', errorMessage);
      setError(errorMessage);
      setTimezoneData({
        timezone: 'UTC',
        dstOffset: 0,
        rawOffset: 0,
        status: 'error',
        error: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (enabled && latitude !== null && longitude !== null) {
      fetchData(latitude, longitude);
    }
  }, [latitude, longitude, enabled]);

  return {
    timezoneData,
    isLoading,
    error,
    refetch: () => {
      if (latitude !== null && longitude !== null) {
        fetchData(latitude, longitude);
      }
    }
  };
}

/**
 * Utility function to convert timezone offset to UTC offset string
 * 
 * @param offsetSeconds - Offset in seconds
 * @returns Formatted offset string (e.g., "UTC+05:30", "UTC-08:00")
 */
export function formatTimezoneOffset(offsetSeconds: number): string {
  const hours = Math.floor(Math.abs(offsetSeconds) / 3600);
  const minutes = Math.floor((Math.abs(offsetSeconds) % 3600) / 60);
  const sign = offsetSeconds >= 0 ? '+' : '-';
  
  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');
  
  return `UTC${sign}${hoursStr}:${minutesStr}`;
}

/**
 * Utility function to check if a location is currently in daylight saving time
 * 
 * @param dstOffset - DST offset in seconds
 * @returns Boolean indicating if DST is currently active
 */
export function isDSTActive(dstOffset: number): boolean {
  return dstOffset !== 0;
}

/**
 * React hook for getting current timezone information for user location
 * Uses browser geolocation API
 * 
 * @param enabled - Whether to automatically get user location (default: true)
 * @returns Object containing timezone data, location data, loading state, and error state
 */
export function useUserTimezone(enabled: boolean = true) {
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  
  const timezoneHook = useTimezone(
    location?.lat || null,
    location?.lng || null,
    enabled && !!location
  );

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      const error = 'Geolocation is not supported by this browser';
      console.error('❌ Geolocation not supported');
      setLocationError(error);
      return;
    }

    console.log('📍 Requesting current user location...');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        console.log('✅ User location obtained:', { latitude, longitude });
        setLocation({ lat: latitude, lng: longitude });
        setLocationError(null);
      },
      (error) => {
        console.error('❌ Geolocation error:', error);
        let errorMessage = 'Failed to get location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        
        setLocationError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  useEffect(() => {
    if (enabled) {
      getCurrentLocation();
    }
  }, [enabled]);

  return {
    ...timezoneHook,
    location,
    locationError,
    getCurrentLocation
  };
}